import { Box, Paper } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useGetAssignableRecipientsQuery } from '@api/recipient';
import {
  AssignableRecipient,
  Piece,
  RecipientAssignableListParams,
  RecipientAutoDetection,
  RecipientAutoDetectionStatus,
  User,
  UserMin,
} from '@apiContract';
import { useAssignmentRecipient } from '@components/Capture/hooks/useAssignmentRecipient';
import { AddEvent } from '@components/Capture/hooks/useUpdateQueue';
import Search from '@uiKit/SearchInput';
import noop from 'lodash/noop';

import { isAssignableRecipient } from '@utils/recipient';

import Footer from '../Footer';
import Title from '../Title';
import AssignTO from './components/AssignTo';
import List from './components/List';
import UserForm from './components/UserForm';
import { sxP20, sxPaper, sxTab, sxTabsSection } from './styles';

const ITEMS_PER_PAGE = 20;

const initialFilters = {
  ItemsPerPage: ITEMS_PER_PAGE,
  PageNumber: 0,
  SearchText: '',
};

type UserListData = {
  piece?: Piece;
  pieceDetection?: RecipientAutoDetection;
  error: unknown;
  loading: boolean;
};

type UserListProps = {
  darkMode?: boolean;
  file: UserListData | null;
  onClose: () => void;
  addEvent?: (param: AddEvent) => void;
  pieces?: Piece[];
  ignoreHeaderLoading?: boolean;
  customSave?: (user: AssignableRecipient) => void;
  withoutUserCreation?: boolean;
  filterOwner?: boolean;
  title?: string;
  saveButtonName?: string;
  isForward?: boolean;
};

const getBulkCardDisplay = (pieces: Piece[]): { user: Piece['recipient']; unassignedText: string } => {
  if (!pieces.length) return { user: undefined, unassignedText: 'Unassigned' };

  const firstRecipient = pieces[0].recipient;
  const allSame = pieces.every((p) => p.recipient?.user?.id === firstRecipient?.user?.id);

  if (allSame) return { user: firstRecipient, unassignedText: 'Unassigned' };

  return { user: undefined, unassignedText: 'Multiple' };
};

const AssignmentList = ({
  file,
  onClose,
  addEvent = noop,
  darkMode,
  pieces: bulkPieces,
  ignoreHeaderLoading,
  customSave,
  withoutUserCreation = false,
  filterOwner,
  title = 'Assign',
  saveButtonName,
  isForward,
}: UserListProps) => {
  const isBulk = !!bulkPieces?.length;
  const { pieceDetection, piece, error, loading } = file || {};
  const isPieceGroup = !!piece?.recipient?.group;
  const autoDetection = pieceDetection || piece?.recipientAutoDetection;
  const bulkCardDisplay = isBulk ? getBulkCardDisplay(bulkPieces) : null;
  const [isAutoDetectionApplied, setIsAutoDetectionApplied] = useState(!!autoDetection && !isBulk);
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedUser, setSelectedUser] = useState<AssignableRecipient | User | UserMin | undefined | null>(
    (!filterOwner &&
      (isBulk
        ? bulkCardDisplay?.user || bulkCardDisplay?.user?.user
        : piece?.recipient || (isAutoDetectionApplied && autoDetection?.recipient))) ||
      null,
  );

  const savedFilters = useMemo(() => {
    if (!filterOwner) return {};
    if (isPieceGroup) {
      return {
        ExcludeGroupId: true,
        GroupId: piece?.recipient?.groupId || undefined,
      };
    }
    return {
      ExcludeUserIds: piece?.recipient?.userId ? [piece.recipient.userId] : [],
    };
  }, [filterOwner, piece?.recipient?.userId, isPieceGroup, piece?.recipient?.groupId]);

  const [filters, setFilters] = useState<RecipientAssignableListParams>({
    ...initialFilters,
    SearchText: (isAutoDetectionApplied && !selectedUser && autoDetection?.recipientSearchString) || '',
    ...savedFilters,
  });
  const [items, setItems] = useState<Array<AssignableRecipient>>([]);
  const { data, isFetching } = useGetAssignableRecipientsQuery(filters);
  const { assignRecipientToPiece } = useAssignmentRecipient(addEvent);

  const loadNextPage = useCallback(
    (startIndex: number) => {
      setFilters((prev) => ({
        ...prev,
        PageNumber: Math.floor(startIndex / ITEMS_PER_PAGE),
        ItemsPerPage: ITEMS_PER_PAGE,
        ...savedFilters,
      }));
    },
    [savedFilters],
  );

  const selectUser = (user: AssignableRecipient) => {
    setSelectedUser(user);
  };

  const getSelectedId = () => {
    if (!selectedUser) return null;
    if (isAssignableRecipient(selectedUser)) return selectedUser.userId || String(selectedUser.groupId);
    return 'id' in selectedUser ? selectedUser.id : null;
  };

  useEffect(() => {
    if (data?.pageNumber === 0) {
      setItems(data.items || []);
    } else {
      setItems((prev) => [...prev, ...(data?.items || [])]);
    }
  }, [data]);

  useEffect(() => {
    if (
      !selectedUser &&
      autoDetection?.recipient &&
      autoDetection?.status === RecipientAutoDetectionStatus.IdentifiedVerificationRequired
    ) {
      setSelectedUser(autoDetection.recipient);
    }
  }, [selectedUser, autoDetection?.recipient, autoDetection?.status]);

  const onSearch = (value: string) => {
    setFilters({ ...initialFilters, ...savedFilters, SearchText: value, PageNumber: 0 });
  };

  const changeTab = () => setTabIndex((prev) => (!prev ? 1 : 0));

  const hasNextPage = (data?.totalCount || 0) > items.length;

  const addUserToPiece = (user?: User) => {
    const currentUser = user || selectedUser;

    if (isBulk && bulkPieces && currentUser) {
      bulkPieces.forEach((p) => assignRecipientToPiece(currentUser, p));
    } else if (piece && currentUser) {
      assignRecipientToPiece(currentUser, piece);
    }

    if (user) {
      setSelectedUser(user);
    }
  };

  const handleSubmit = () => {
    if (customSave) {
      customSave(selectedUser as AssignableRecipient);
    } else {
      addUserToPiece();
    }
    onClose();
  };

  const applyAutoDetection = () => {
    setIsAutoDetectionApplied(true);
    if (autoDetection?.recipient) {
      setSelectedUser(autoDetection?.recipient);
    }

    if (autoDetection?.recipientSearchString) {
      onSearch(autoDetection?.recipientSearchString);
    }
  };

  const selectedId = getSelectedId();

  const isConfirm =
    !isBulk &&
    !!selectedUser &&
    selectedId === autoDetection?.recipient?.id &&
    !piece?.recipient &&
    !!autoDetection?.status &&
    [
      RecipientAutoDetectionStatus.Identified,
      RecipientAutoDetectionStatus.IdentifiedBasedOnPreviousAssignment,
      RecipientAutoDetectionStatus.IdentifiedVerificationRequired,
    ].includes(autoDetection.status);

  const footerDisabled = isBulk
    ? !selectedId || (!!bulkCardDisplay?.user && selectedId === bulkCardDisplay.user?.userId)
    : !selectedId || selectedId === piece?.recipient?.userId;

  return (
    <Paper elevation={3} sx={sxPaper}>
      <Title
        title={title}
        onClose={onClose}
        isLoading={ignoreHeaderLoading ? false : !isBulk && (loading || !piece?.recipientAutoDetection)}
      />
      <Box component="section" sx={sxTabsSection(tabIndex)}>
        <Box display="flex" sx={sxTab}>
          <AssignTO
            user={selectedUser}
            recipientDetection={isBulk ? undefined : autoDetection}
            applyAutoDetection={isBulk ? undefined : applyAutoDetection}
            piece={isBulk ? undefined : piece}
            unassignedText={bulkCardDisplay?.unassignedText || 'No Recipient set'}
            showAlert={
              !isBulk &&
              !error &&
              !!autoDetection &&
              !piece?.recipient?.groupId &&
              (!piece?.recipient ||
                (autoDetection?.recipientId === piece?.recipient?.userId &&
                  autoDetection?.status !== RecipientAutoDetectionStatus.IdentifiedVerificationRequired))
            }
          />
          <Search setSearchResult={onSearch} sx={sxP20} defaultSearch={filters.SearchText} isLoading={isFetching} />
          <List
            darkMode={darkMode}
            changeTab={withoutUserCreation ? undefined : changeTab}
            loadNextPage={loadNextPage}
            isNextPageLoading={isFetching}
            items={items}
            setSelectedUser={selectUser}
            selectedUser={selectedUser}
            hasNextPage={hasNextPage}
            isForward={isForward}
          />
          <Footer
            sx={sxP20}
            disabled={filterOwner ? !selectedId : footerDisabled}
            title={saveButtonName || (isConfirm ? 'Confirm Recipient' : 'Save Changes')}
            handleSubmit={handleSubmit}
          />
        </Box>
        {!withoutUserCreation && (
          <UserForm changeTab={changeTab} assignUser={addUserToPiece} defaultSearch={filters.SearchText} />
        )}
      </Box>
    </Paper>
  );
};

export default AssignmentList;
